import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Search } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { Calendar } from "@/frontend/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/frontend/components/ui/popover";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/frontend/components/ui/pagination";
import { ResidenceCard } from "@/frontend/components/residences/ResidenceCard";
import { AvailableRoomsList } from "@/frontend/components/booking/AvailableRoomsList";
import { useResidences } from "@/backend/hooks/useResidences";
import { useAvailableRooms } from "@/backend/hooks/useAvailableRooms";
import { cn } from "@/frontend/lib/utils";

const PAGE_SIZE = 8;

export function ResidencesPreview() {
  const navigate = useNavigate();
  const { data: residences = [], isLoading } = useResidences(true);
  const previewResidences = residences.slice(0, 4);

  const [draftDate, setDraftDate] = useState<Date | undefined>(undefined);
  const [checkIn, setCheckIn] = useState<Date | undefined>(undefined);
  const [page, setPage] = useState(1);

  const { data: availableRooms = [], isFetching } = useAvailableRooms(checkIn);

  const totalPages = Math.max(1, Math.ceil(availableRooms.length / PAGE_SIZE));
  const pagedRooms = availableRooms.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = () => {
    setCheckIn(draftDate);
    setPage(1);
  };

  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleSelectRoom = (room: { residenceId: string; roomTypeId: string }) => {
    if (!checkIn) return;
    navigate(
      `/book?residence=${room.residenceId}&roomType=${room.roomTypeId}&checkIn=${format(checkIn, "yyyy-MM-dd")}`
    );
  };

  return (
    <section className="pt-16 md:pt-24 pb-8 md:pb-10 overflow-x-clip">
      <div className="container-narrow">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
            Our Residences
          </h2>
        </div>

        <div className="max-w-xl mx-auto mb-10 flex flex-col sm:flex-row gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "flex-1 justify-start text-left font-normal",
                  !draftDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {draftDate ? format(draftDate, "PPP") : "Pick a move-in date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={draftDate}
                onSelect={setDraftDate}
                disabled={isPastDate}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          <Button
            type="button"
            onClick={handleSearch}
            disabled={!draftDate}
            className="sm:w-auto"
          >
            <Search className="mr-2 h-4 w-4" />
            Search
          </Button>
        </div>

        {checkIn ? (
          <>
            <AvailableRoomsList
              rooms={pagedRooms}
              onSelect={handleSelectRoom}
              isLoading={isFetching}
            />
            {totalPages > 1 && (
              <Pagination className="mt-8">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.max(1, p - 1));
                      }}
                      aria-disabled={page === 1}
                      className={cn(page === 1 && "pointer-events-none opacity-50")}
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <PaginationItem key={p}>
                      <PaginationLink
                        href="#"
                        isActive={p === page}
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(p);
                        }}
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.min(totalPages, p + 1));
                      }}
                      aria-disabled={page === totalPages}
                      className={cn(page === totalPages && "pointer-events-none opacity-50")}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        ) : (
          <div className="lg:mx-[calc(50%-50vw)] lg:px-8 xl:px-16">
            <div className="mx-auto grid max-w-screen-2xl grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {isLoading ? (
                <div className="col-span-full flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {previewResidences.map((residence, index) => (
                    <ResidenceCard
                      key={residence.id}
                      residence={residence}
                      index={index}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
